import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type {
  PopulatedInteraction,
  TaskWithPopulatedAssignees,
  KeyResult,
  EmployeePopulatedGoalIssue,
} from '@momentum/types';
import { IssueStatus, KeyResultType } from '@momentum/types';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { useDateFormatter } from '@/shared/hooks/useDateFormatter';
import { useUpdateGoal } from '@/shared/hooks/useGoals';
import {
  HeartHandshake,
  MessageCircleMore,
  ClipboardList,
  CalendarPlus,
  CalendarCheck,
  AlertCircle,
  Edit3,
  Plus,
  Users,
  Repeat,
  CheckCircle2,
} from 'lucide-react';
import { GoalInteractionsList } from './GoalInteractionsList';
import { GoalTasksTable } from './GoalTasksTable';
import { InlineStatusSelector } from './InlineStatusSelector';
import { KPIProgressUpdate } from '@/features/goals/hierarchy-tree/goal-card/components/KPIProgressUpdate';
import { KPIProgressValue } from '@/features/goals/shared/KPIProgressValue';
import { CircularProgress } from '@/features/goals/shared/CircularProgress';
import { isOverdue } from '../utils/overdue.utils';
import { UpdateGoalModalForReview } from './UpdateGoalModalForReview';
import { CreateTaskModalForGoal } from './CreateTaskModalForGoal';
import { KeyResultHistoryDrawer } from '@/features/goals/goal-view/components/KeyResultHistoryDrawer';

interface GoalReviewCardProps {
  goal: EmployeePopulatedGoalIssue;
  interactions: PopulatedInteraction[];
  tasks: TaskWithPopulatedAssignees[];
  currentEmployeeId: string;
  onCreateInteraction: (type: 'praise' | 'feedback' | 'debrief') => void;
  lastOneOnOneDate?: string | null;
}

export const GoalReviewCard = ({
  goal,
  interactions,
  tasks,
  currentEmployeeId,
  onCreateInteraction,
  lastOneOnOneDate,
}: GoalReviewCardProps) => {
  const { t } = useTranslation('oneOnOne');
  const { t: tCommon } = useTranslation('common');
  const { t: tGoals } = useTranslation('goals');
  const { formatDate } = useDateFormatter();
  const updateGoalMutation = useUpdateGoal();
  const [openKpiPopover, setOpenKpiPopover] = useState<number | null>(null);

  const [selectedKeyResult, setSelectedKeyResult] = useState<KeyResult | null>(
    null
  );
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);

  const hasKeyResults = Boolean(goal.keyResults && goal.keyResults.length > 0);
  const goalIsOverdue = isOverdue(goal);

  const handleStatusChange = useCallback(
    async (newStatus: IssueStatus) => {
      try {
        await updateGoalMutation.mutateAsync({
          id: goal._id,
          data: { status: newStatus },
        });
      } catch (error) {
        toast.error(t('failedToUpdateStatus'), {
          description:
            error instanceof Error
              ? error.message
              : t('couldNotUpdateGoalStatus'),
        });
      }
    },
    [goal._id, updateGoalMutation, t]
  );

  const handleViewHistory = (kr: KeyResult) => {
    setSelectedKeyResult(kr);
    setIsHistoryDrawerOpen(true);
  };

  const goalDates = useMemo(() => {
    const dates: Array<{ label: string; date: string; isStart: boolean }> = [];

    if (goal.startDate) {
      dates.push({
        label: t('start'),
        date: formatDate(new Date(goal.startDate), 'medium'),
        isStart: true,
      });
    }

    if (goal.dueDate) {
      dates.push({
        label: t('due'),
        date: formatDate(new Date(goal.dueDate), 'medium'),
        isStart: false,
      });
    }

    return dates;
  }, [goal.startDate, goal.dueDate, formatDate, t]);

  return (
    <Card
      className={`border-none bg-white/90 shadow-lg backdrop-blur ${
        goalIsOverdue ? 'border-2 border-red-300 bg-red-50/50' : 'border-none'
      }`}
    >
      {/* Goal Header */}
      <CardHeader className="space-y-3">
        {/* Completed Goal Badge */}
        {goal.status === IssueStatus.COMPLETED && goal.metadata?.closedAt && (
          <Badge
            variant="secondary"
            className="w-fit bg-green-100 text-green-800 hover:bg-green-100"
          >
            <CheckCircle2 className="w-3 h-3 me-1" />
            {(t('completedOn') as ({ date }: { date: string }) => string)({
              date: formatDate(new Date(goal.metadata.closedAt), 'medium'),
            })}
          </Badge>
        )}

        <div className="flex flex-wrap items-start justify-between gap-2">
          <CardTitle className="text-2xl font-semibold text-slate-900">
            {goal.title}
          </CardTitle>
          <div className="flex items-center gap-2">
            <UpdateGoalModalForReview goal={goal} />
            <InlineStatusSelector
              value={goal.status}
              onValueChange={handleStatusChange}
              disabled={updateGoalMutation.isPending}
            />
          </div>
        </div>

        {goal.description && (
          <p className="text-base leading-relaxed text-muted-foreground text-start">
            {goal.description}
          </p>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Owner and assignees */}
        <div className="flex items-center justify-start gap-1">
          <section className="flex items-center gap-1 pl-4">
            <span className="text-sm font-medium text-foreground ml-1 flex items-center gap-1">
              <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              {tCommon('creator')}:
            </span>
            <span className="text-sm font-medium text-foreground">
              {`${goal.creator.personalInfo.firstName} ${goal.creator.personalInfo.lastName}`}
            </span>
          </section>
          <section className="flex items-center gap-1">
            <span className="text-sm font-medium text-foreground ml-1 flex items-center gap-1">
              <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              {tGoals('assigneesLabel')}:
            </span>
            <span className="text-sm font-medium text-foreground">
              {goal.assignees
                .map(
                  (assignee) =>
                    `${assignee?.personalInfo?.firstName} ${
                      assignee?.personalInfo?.lastName ?? ''
                    }`
                )
                .join(', ')}
            </span>
          </section>
        </div>

        {/* Goal Dates */}
        {goal.startDate || goal.dueDate ? (
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            {goalDates.map((dateInfo) => {
              const isDueDate = !dateInfo.isStart;
              const isOverdueDate = isDueDate && goalIsOverdue;

              return (
                <span
                  key={dateInfo.label}
                  className={`inline-flex items-center gap-2 ${
                    isOverdueDate ? 'text-red-700' : ''
                  }`}
                >
                  {dateInfo.isStart ? (
                    <CalendarPlus className="h-4 w-4 text-primary" />
                  ) : (
                    <>
                      {isOverdueDate && (
                        <AlertCircle className="h-4 w-4 text-red-600" />
                      )}
                      <CalendarCheck
                        className={`h-4 w-4 ${
                          isOverdueDate ? 'text-red-600' : 'text-primary'
                        }`}
                      />
                    </>
                  )}
                  <span className="font-medium">
                    {dateInfo.label}: {dateInfo.date}
                  </span>
                </span>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic px-3 py-2 bg-slate-50 rounded-md border border-slate-200">
            {t('noDatesSet')}
          </p>
        )}

        {/* Key Results Section */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 text-start">
            {t('keyResults')}
          </h3>
          {hasKeyResults ? (
            <div className="space-y-3">
              {(() => {
                // Separate KPIs by type
                // Fallback to MAXIMUM for existing KPIs without type field
                const maximumKPIs = (goal.keyResults || []).filter(
                  (kr) =>
                    (kr.type ?? KeyResultType.MAXIMUM) === KeyResultType.MAXIMUM
                );
                const minimumKPIs = (goal.keyResults || []).filter(
                  (kr) =>
                    (kr.type ?? KeyResultType.MAXIMUM) === KeyResultType.MINIMUM
                );

                const renderKPI = (kr: KeyResult, index: number) => {
                  const originalIndex = goal.keyResults?.indexOf(kr) ?? index;
                  const isMinimum =
                    (kr.type ?? KeyResultType.MAXIMUM) ===
                    KeyResultType.MINIMUM;
                  const hasProgress =
                    kr.currentProgress !== undefined &&
                    kr.quantity !== undefined;

                  // For minimum KPIs (like complaints), reverse the progress calculation
                  // If you have 7 complaints out of 10, that's 30% progress (3 remaining)
                  // Guard against division by zero and clamp to 0-100 range
                  const progress =
                    hasProgress && kr.quantity > 0
                      ? Math.min(
                          Math.max(
                            isMinimum
                              ? ((kr.quantity - kr.currentProgress) /
                                  kr.quantity) *
                                  100
                              : (kr.currentProgress / kr.quantity) * 100,
                            0
                          ),
                          100
                        )
                      : 0;

                  return (
                    <div key={index} className="space-y-1.5 group/kpi">
                      {/* Recurring Icon Badge */}
                      {kr.recurrence && (
                        <div className="flex items-center gap-2 mb-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-auto p-1 text-primary hover:bg-primary/10"
                            onClick={() => handleViewHistory(kr)}
                          >
                            <Repeat className="h-4 w-4 me-1" />
                            <span className="text-xs">
                              {tGoals('viewHistory')}
                            </span>
                          </Button>
                        </div>
                      )}
                      {/* Circular Progress and Title/Value */}
                      <div className="flex items-center gap-6">
                        <CircularProgress
                          progress={progress}
                          startDate={goal.startDate}
                          dueDate={goal.dueDate}
                          size={48}
                          strokeWidth={5}
                          isMinimum={isMinimum}
                        />
                        {/* Title and Value stacked */}
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-700">
                              {kr.title}
                            </span>
                            {hasProgress && (
                              <div onClick={(e) => e.stopPropagation()}>
                                <KPIProgressUpdate
                                  goalId={goal._id}
                                  keyResult={kr}
                                  keyResultIndex={originalIndex}
                                  allKeyResults={goal.keyResults || []}
                                  isOpen={openKpiPopover === originalIndex}
                                  onOpenChange={(open) =>
                                    setOpenKpiPopover(
                                      open ? originalIndex : null
                                    )
                                  }
                                >
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 opacity-0 group-hover/kpi:opacity-100 transition-opacity"
                                  >
                                    <Edit3 className="h-3 w-3" />
                                  </Button>
                                </KPIProgressUpdate>
                              </div>
                            )}
                          </div>
                          {hasProgress && (
                            <KPIProgressValue
                              currentProgress={kr.currentProgress}
                              quantity={kr.quantity}
                              unit={kr.unit}
                              variant="inline"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                };

                return (
                  <>
                    {maximumKPIs.map((kr, index) => renderKPI(kr, index))}
                    {minimumKPIs.map((kr, index) =>
                      renderKPI(kr, maximumKPIs.length + index)
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic px-3 py-2 bg-slate-50 rounded-md border border-slate-200">
              {t('noKeyResultsDefined')}
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200" />

        {/* Related Tasks Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 text-start">
              {t('relatedTasks')}
            </h3>
            <CreateTaskModalForGoal
              parentGoalId={goal._id}
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 px-2.5 text-xs font-medium"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {tGoals('createTaskButton')}
                </Button>
              }
            />
          </div>
          <GoalTasksTable
            tasks={tasks}
            goalId={goal._id}
            currentEmployeeId={currentEmployeeId}
          />
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200" />

        {/* Interaction Creation Buttons */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 text-start mb-3">
            {t('createInteractionForGoal')}
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Button
              onClick={() => onCreateInteraction('praise')}
              variant="outline"
              className="h-auto flex items-center justify-center gap-2 border-2 bg-white hover:border-[#0bb980] hover:bg-[#0bb980]/10"
            >
              <HeartHandshake className="h-5 w-5 text-[#0bb980]" />
              <span className="text-sm font-semibold text-slate-900">
                {tCommon('createPraise')}
              </span>
            </Button>

            <Button
              onClick={() => onCreateInteraction('feedback')}
              variant="outline"
              className="h-auto flex items-center justify-center gap-2 border-2 bg-white hover:border-[#3b83f6] hover:bg-[#3b83f6]/10"
            >
              <MessageCircleMore className="h-5 w-5 text-[#3b83f6]" />
              <span className="text-sm font-semibold text-slate-900">
                {tCommon('createFeedback')}
              </span>
            </Button>

            <Button
              onClick={() => onCreateInteraction('debrief')}
              variant="outline"
              className="h-auto flex items-center justify-center gap-2 border-2 bg-white hover:border-[#6465f0] hover:bg-[#6465f0]/10"
            >
              <ClipboardList className="h-5 w-5 text-[#6465f0]" />
              <span className="text-sm font-semibold text-slate-900">
                {tCommon('createDebrief')}
              </span>
            </Button>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-200" />

        {/* Previous Interactions */}
        <GoalInteractionsList
          interactions={interactions}
          currentEmployeeId={currentEmployeeId}
          lastOneOnOneDate={lastOneOnOneDate}
        />
      </CardContent>

      {/* History Drawer */}
      {selectedKeyResult && (
        <KeyResultHistoryDrawer
          keyResult={selectedKeyResult}
          isOpen={isHistoryDrawerOpen}
          onClose={() => {
            setIsHistoryDrawerOpen(false);
            setSelectedKeyResult(null);
          }}
        />
      )}
    </Card>
  );
};
